import { memo, ReactNode } from 'react';
import {
  Box,
  Container,
  FormField,
  Header,
  KeyValuePairs,
  Select,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-core';
import { ContentSubType, ContentType } from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import { MetadataTabFieldInputView } from './MetadataTabFieldInputView';
import { MetadataTabViewModel } from './MetadataTabViewModel';
import { ContentTypeOptions, PatternTypeOptions } from '../utils/SelectableOptions';

const CONTENT_SUB_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(ContentSubType));
const CONTENT_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(ContentType));

function isContentSubType(value: string): value is ContentSubType {
  return CONTENT_SUB_TYPE_VALUES.has(value);
}

function isContentType(value: string): value is ContentType {
  return CONTENT_TYPE_VALUES.has(value);
}

interface MetadataTabConfigurationViewProps {
  readonly vm: MetadataTabViewModel;
  /** Called when the user picks a different content type. The parent shows a confirmation modal before invoking the VM. */
  readonly onContentTypeRequest: (next: ContentType) => void;
}

/**
 * Renders the Configuration container — read-only general info (content
 * type, version, authors, last published) plus the schema-driven metadata
 * grid (main technology, AWS services, workload, etc.).
 */
export const MetadataTabConfigurationView = memo(function MetadataTabConfigurationView({
  vm,
  onContentTypeRequest,
}: MetadataTabConfigurationViewProps) {
  // Strategy content is not supported in v2; hide it from the picker.
  const contentTypeOptions = ContentTypeOptions.filter((o) => o.value !== ContentType.Strategy);
  const selectedContentType = contentTypeOptions.find((o) => o.value === vm.contentType) ?? null;
  const selectedSubType = PatternTypeOptions.find((o) => o.value === vm.contentSubType) ?? null;

  // General-info rows: content type (interactive), then read-only metadata.
  const isStrategy = vm.contentType === ContentType.Strategy;
  const generalItems: { label: ReactNode; value: ReactNode }[] = [
    {
      label: 'Content type',
      value: isStrategy ? (
        <SpaceBetween size="xxs">
          <Box>Strategy</Box>
          <StatusIndicator type="info">
            Strategy is deprecated and will be migrated to Guide automatically.
          </StatusIndicator>
        </SpaceBetween>
      ) : (
        <FormField errorText={vm.fieldErrors.contentType} stretch>
          <Select
            selectedOption={selectedContentType}
            options={contentTypeOptions}
            onChange={({ detail }) => {
              const next = detail.selectedOption?.value;
              if (!next) return;
              if (!isContentType(next)) {
                throw new Error(`Unexpected ContentType value: ${next}`);
              }
              if (next !== vm.contentType) onContentTypeRequest(next);
            }}
          />
        </FormField>
      ),
    },
  ];

  // Pattern-only sub-type input sits next to Content type when applicable.
  if (vm.contentType === ContentType.Pattern) {
    generalItems.push({
      label: 'Pattern type',
      value: (
        <Select
          selectedOption={selectedSubType}
          options={PatternTypeOptions}
          placeholder="Choose pattern type"
          onChange={({ detail }) => {
            const nextSubType = detail.selectedOption?.value;
            if (!nextSubType) return;
            if (!isContentSubType(nextSubType)) {
              throw new Error(`Unexpected ContentSubType value: ${nextSubType}`);
            }
            vm.changeContentSubType(nextSubType);
          }}
        />
      ),
    });
  }

  generalItems.push(
    { label: 'Version', value: vm.version ?? <Box color="text-body-secondary">—</Box> },
    {
      label: 'Authors',
      value: vm.authorsByline || <Box color="text-body-secondary">—</Box>,
    },
    {
      label: 'Last published',
      value: vm.lastPublicationDate ? (
        vm.lastPublicationDate.toLocaleString(DateTime.DATE_FULL)
      ) : (
        <Box color="text-body-secondary">Never</Box>
      ),
    },
  );

  return (
    <Container header={<Header variant="h3">Configuration</Header>}>
      <SpaceBetween size="l">
        <KeyValuePairs columns={2} items={generalItems} />
        {vm.metadataFields.length > 0 && (
          <KeyValuePairs
            columns={2}
            items={vm.metadataFields.map((field) => ({
              label: field.isRequired ? (
                field.label
              ) : (
                <>
                  {field.label} <i>- optional</i>
                </>
              ),
              value: (
                <MetadataTabFieldInputView
                  field={field}
                  errorText={vm.fieldErrors.templateFields[field.key]}
                  onChange={(next) => vm.changeMetadataField(field, next)}
                />
              ),
            }))}
          />
        )}
      </SpaceBetween>
    </Container>
  );
});
